/**
 * What was eaten and what it came to, per `/specs/features/nutrition.spec.md`: foods, the
 * amounts they are logged in, the entries a day holds and the goal those are read against. The
 * shapes are the server's own, and the arithmetic between them is the same the server does, so
 * what a sheet says an amount will come to is what the entry says once it is saved. The
 * expenditure the goal is worked out from is `expenditure.ts`, worked out here from all of it.
 */

import type { PutEntryBody } from '~/api/endpoints'
import { laterOf, updatedAtOf } from '~/data/sync'
import {
  CALORIE_FLOORS,
  type CalorieFloor,
  type CalorieLevel,
  type Diet,
  type GuidedTargets as CoachedWeek,
  type ProteinIntake,
} from '@gymcloud/nutrition'
import type { PartOfTheDay } from '@gymcloud/nutrition/order'

import type { Units } from '~/data/types'
import { dayKey, dayStart } from '~/lib/dates'
import { kgToDisplay } from '~/lib/units'
import { weekdayOf } from '~/lib/weekdays'

export const NUTRIENTS = [
  'calories',
  'protein',
  'carbs',
  'fat',
  'fibre',
  'sugar',
  'saturatedFat',
  'sodium',
] as const

export type Nutrient = (typeof NUTRIENTS)[number]

/** The three the goal targets, in the order they are read everywhere. */
export const MACROS = ['protein', 'carbs', 'fat'] as const

export type Macro = (typeof MACROS)[number]

/** The four a day is headed by, calories first. */
export const TARGETED = ['calories', ...MACROS] as const

export type Targeted = (typeof TARGETED)[number]

export const NUTRIENT_LABELS: Record<Nutrient, string> = {
  calories: 'Calories',
  protein: 'Protein',
  carbs: 'Carbs',
  fat: 'Fat',
  fibre: 'Fibre',
  sugar: 'Sugar',
  saturatedFat: 'Saturated fat',
  sodium: 'Sodium',
}

/** What each nutrient is counted in. */
export const NUTRIENT_UNITS: Record<Nutrient, string> = {
  calories: 'kcal',
  protein: 'g',
  carbs: 'g',
  fat: 'g',
  fibre: 'g',
  sugar: 'g',
  saturatedFat: 'g',
  sodium: 'mg',
}

/** The colour each targeted nutrient wears wherever it is met — see the theme. */
export const NUTRIENT_COLORS: Record<Targeted, string> = {
  calories: 'var(--color-metric-calories)',
  protein: 'var(--color-metric-protein)',
  carbs: 'var(--color-metric-carbs)',
  fat: 'var(--color-metric-fat)',
}

/**
 * The same four, deepened: what is past a target is drawn in this cut wherever it is drawn —
 * the lap a ring goes round again, the stretch of a bar over the first. Never the danger red:
 * eating over what a goal asks for is a reading rather than a fault.
 */
export const NUTRIENT_DEEP_COLORS: Record<Targeted, string> = {
  calories: 'var(--color-metric-calories-deep)',
  protein: 'var(--color-metric-protein-deep)',
  carbs: 'var(--color-metric-carbs-deep)',
  fat: 'var(--color-metric-fat-deep)',
}

/** What a gram of each macro comes to in calories — the same three numbers every app uses. */
export const CALORIES_PER_GRAM: Record<Macro, number> = { protein: 4, carbs: 4, fat: 9 }

/**
 * What share of something's calories a macro is — the line under a macro's dial. Null where
 * the macro is unknown, or where there are no calories to be a share of.
 */
export function macroShare(nutrients: Nutrients, macro: Macro): number | null {
  const grams = nutrients[macro]
  if (grams === null || nutrients.calories <= 0) {
    return null
  }
  return Math.min(1, Math.max(0, (grams * CALORIES_PER_GRAM[macro]) / nutrients.calories))
}

/**
 * How far past its target something went, as a share of a whole target — the lap drawn again
 * over the first. Nought inside the target, and never more than one whole turn.
 */
export function pastTargetLap(eaten: number, target: number | null): number {
  if (target === null || target <= 0 || eaten <= target) {
    return 0
  }
  return Math.min(1, (eaten - target) / target)
}

/** Calories are the one nutrient a food cannot be without; the rest may be unknown. */
export type Nutrients = { calories: number } & Record<Exclude<Nutrient, 'calories'>, number | null>

export type Reference = '100g' | '100ml' | 'portion'

/**
 * What each unit an amount can be taken in comes to, in grams or in millilitres. The numbers
 * are the specification's, and every port holds the same table: an amount is stored in the
 * unit it was typed in, so what it comes to is worked out from here every time it is read.
 *
 * A fluid ounce is thirty millilitres rather than the American 29.5735, so that a cup is
 * eight of them exactly and two units offered side by side never disagree.
 */
export const MEASURES = {
  g: { measure: 'weight', per: 1 },
  oz: { measure: 'weight', per: 28.3495 },
  lb: { measure: 'weight', per: 453.592 },
  ml: { measure: 'volume', per: 1 },
  tsp: { measure: 'volume', per: 5 },
  tbsp: { measure: 'volume', per: 15 },
  'fl oz': { measure: 'volume', per: 30 },
  cup: { measure: 'volume', per: 240 },
} as const satisfies Record<string, { measure: 'weight' | 'volume'; per: number }>

/** A unit a weight or a volume is taken in, portions aside. */
export type BulkUnit = keyof typeof MEASURES

export type AmountUnit = BulkUnit | 'portion'

export interface Portion {
  readonly name: string
  readonly amount: number
  readonly unit: 'g' | 'ml'
}

export interface Amount {
  readonly value: number
  readonly unit: AmountUnit
  /** Which portion, where the unit is one. */
  readonly portion: string | null
}

export interface Ingredient {
  readonly name: string
  readonly food: Food | null
  readonly amount: Amount
  /** What this much of it came to, in total. */
  readonly nutrients: Nutrients
}

export interface Recipe {
  readonly servings: number
  readonly ingredients: readonly Ingredient[]
  /**
   * What the whole recipe weighs in grams, where whoever wrote it said — the nutrition spec,
   * What a recipe weighs. It is what makes a recipe loggable by weight whatever its ingredients
   * are counted in: a serving is this divided by the servings.
   */
  readonly totalGrams?: number | null
}

/** Which database a library food came from — the sources spec. */
export type Library = 'openfoodfacts' | 'usda'

export interface Food {
  /** Null on a library food, which has no row of its own. */
  readonly id: string | null
  readonly source: 'own' | 'library'
  /** Which database a library food came from; null on an own food. */
  readonly library: Library | null
  /**
   * The app this food came in from, where it came in from one: `macrofactor`. Null on
   * everything made here, and never cleared by an edit. A food carrying this and no id of
   * its own is the copy an imported entry keeps: the server offers it in no search and in no
   * recently-logged list, so nothing here has to hide it.
   */
  readonly broughtFrom: string | null
  /** A product's barcode, or `usda:` and a generic food's id, on a library food. */
  readonly libraryCode: string | null
  readonly name: string
  /** The emoji it was given, where one was. Off its name otherwise — see `foodEmoji`. */
  readonly emoji: string | null
  readonly brand: string | null
  readonly barcode: string | null
  readonly reference: Reference
  readonly nutrients: Nutrients
  readonly portions: readonly Portion[]
  /**
   * Grams per millilitre, on a liquid; null where unknown, which is not one. A food carrying
   * one is taken by volume and by weight alike — the nutrition spec, Units.
   */
  readonly density: number | null
  readonly recipe: Recipe | null
  /**
   * When the row was last written, as the server says it — absent on a library food, which is
   * nobody's row. It places one of the account's own that has never been logged: a thing made
   * this morning is a thing you have just done something with, and one made a year ago and
   * never logged is not. See `/specs/features/food-order.spec.md`.
   */
  readonly updatedAt?: string
}

/**
 * Things saved to be logged together, kept as slots — the nutrition spec, Meals. A meal is
 * not a food: it has no reference amount, and what it comes to is what the slots that are on
 * come to together. A meal is its page: what the page shows is what the meal is.
 */
/** One alternative of a slot: one food or one quick entry, in an amount of its own. */
export interface MealAlternative {
  readonly id: string
  readonly member: Ingredient
}

/** One slot of a meal: the alternatives it can be logged as. */
export interface MealSlot {
  readonly id: string
  readonly alternatives: readonly MealAlternative[]
}

/**
 * Where each slot stands: on the alternative named for it, on its first where none is named,
 * or off — on none of them.
 */
export interface MealState {
  readonly chosen: Readonly<Record<string, string>>
  readonly off: readonly string[]
}

export const EMPTY_MEAL_STATE: MealState = { chosen: {}, off: [] }

/**
 * A meal as it stood at the moment it was logged — the nutrition spec, Meal log. `mealId` is
 * null on the log of something logged without being kept: the AI's answer, a shared meal.
 */
export interface MealLog {
  readonly id: string
  readonly mealId: string | null
  readonly name: string
  readonly emoji: string | null
  readonly slots: readonly MealSlot[]
  readonly state: MealState
  readonly loggedAt: number
}

export interface Meal {
  readonly id: string
  readonly name: string
  readonly emoji: string | null
  readonly slots: readonly MealSlot[]
  readonly state: MealState
  /** The alternative every slot that is on stands on, flattened. The slots are the truth. */
  readonly members: readonly Ingredient[]
  /** The last few logs, newest first, and how many there are in all. */
  readonly logs: readonly MealLog[]
  readonly logCount: number
  readonly lastLoggedAt: number | null
  /** When the row was last written — what places a meal that has never been logged. */
  readonly updatedAt?: string
}

/** What a meal comes to: its members together. */
export function mealTotal(members: readonly Ingredient[]): Nutrients {
  return summed(members.map((member) => member.nutrients))
}

/** Whether a slot is off — on none of its alternatives. */
export function slotOff(state: MealState, slot: MealSlot): boolean {
  return state.off.includes(slot.id)
}

/** The alternative a slot stands on: the chosen one where it still exists, the first otherwise. */
export function standingOn(slot: MealSlot, state: MealState): MealAlternative | null {
  return (
    slot.alternatives.find((alt) => alt.id === state.chosen[slot.id]) ??
    slot.alternatives[0] ??
    null
  )
}

/** The alternative of every slot that is on, flattened — what logging logs. */
export function membersOn(slots: readonly MealSlot[], state: MealState): Ingredient[] {
  const members: Ingredient[] = []
  for (const slot of slots) {
    if (slotOff(state, slot)) {
      continue
    }
    const alt = standingOn(slot, state)
    if (alt !== null) {
      members.push(alt.member)
    }
  }
  return members
}

/**
 * A meal, a log or a favourite's kept copy read whatever shape it was written in. One written
 * before slots carries its members and no slots at all, and is read as one slot per member,
 * each standing on its only alternative — the nutrition spec, Meals from before. The server
 * reads its own rows this way; this is for the copies already mirrored on a device, which the
 * feed has no reason to send again.
 */
export function mealAsKept<T extends object>(kept: T): T {
  const held = kept as { slots?: unknown; members?: readonly Ingredient[] }
  if (Array.isArray(held.slots)) {
    return kept
  }
  const members = held.members ?? []
  return {
    ...kept,
    slots: members.map((member, at) => ({
      id: `member-${at}`,
      alternatives: [{ id: `member-${at}`, member }],
    })),
    state: EMPTY_MEAL_STATE,
  }
}

/** A state holding only what the slots hold: a chosen alternative that is gone is forgotten. */
export function stateTrimmedTo(state: MealState, slots: readonly MealSlot[]): MealState {
  const chosen: Record<string, string> = {}
  for (const slot of slots) {
    const alt = state.chosen[slot.id]
    if (alt !== undefined && slot.alternatives.some((each) => each.id === alt)) {
      chosen[slot.id] = alt
    }
  }
  const ids = new Set(slots.map((slot) => slot.id))
  return { chosen, off: state.off.filter((id) => ids.has(id)) }
}

/**
 * What the meal's page would open on — every slot that is on, as the alternative it stands
 * on. This is the total a meal names wherever it stands.
 */
export function mealOpensTo(meal: Meal): Nutrients {
  return mealTotal(membersOn(meal.slots, meal.state))
}

/** A log read as a meal, for a page to open on: how it stood then, under the log's own id. */
export function mealOfLog(log: MealLog): Meal {
  return {
    id: log.id,
    name: log.name,
    emoji: log.emoji,
    slots: log.slots,
    state: log.state,
    members: membersOn(log.slots, log.state),
    logs: [],
    logCount: 0,
    lastLoggedAt: log.loggedAt,
  }
}

/** A food as a search answers it: with whether it is the account's own. */
export interface FoundFood extends Food {
  readonly mine: boolean
}

/** A food logged before, with how it was last logged. */
export interface RecentFood {
  readonly food: Food
  readonly amount: Amount
  readonly times: number
  readonly lastAt: number
  /**
   * How many of its times fell in each part of the day, by the part's own name — what places
   * it in a list of the foods logged before, see `/specs/features/food-order.spec.md`.
   */
  readonly inParts: Partial<Record<PartOfTheDay, number>>
}

export interface Entry {
  readonly id: string
  readonly day: string
  readonly time: string
  readonly name: string
  readonly food: Food | null
  readonly amount: Amount
  readonly nutrients: Nutrients
  readonly createdAt: number
  /** When it was last written, as an ISO-8601 string with milliseconds. */
  readonly updatedAt: string
  /**
   * Logged through a meal. The foods-logged-before list skips one, so that the meal stands in
   * it as itself rather than its members flooding it — see the nutrition spec, Meals. It rides
   * on the row because that list is worked out from the entries now, and the row is the only
   * place the answer could come from.
   */
  readonly fromMeal?: boolean
  /**
   * Which meal it was logged from, where that meal is one the account keeps. None — null, or
   * nothing at all — where there is no meal to name: an entry of its own, an answer logged
   * without being kept, a plate somebody shared. It is what lets what was logged as a meal be
   * read back as one.
   */
  readonly mealId?: string | null
}

export type GoalMode = 'guided' | 'manual'
export type Aim = 'lose' | 'maintain' | 'gain'

export {
  CALORIE_LEVELS,
  DEFAULT_PROGRAM,
  DIETS,
  FLOOR_CALORIES,
  MAINTAIN_WINDOW_KG,
  PROTEIN_G_PER_KG,
  PROTEIN_INTAKES,
  evenDistribution,
  type CalorieLevel,
  type Diet,
  type Program,
  type ProteinIntake,
} from '@gymcloud/nutrition'

export const DIET_LABELS: Record<Diet, string> = {
  balanced: 'Balanced',
  'low-fat': 'Low fat',
  'low-carb': 'Low carb',
  keto: 'Keto',
}

export const CALORIE_FLOOR_CHOICES = CALORIE_FLOORS
export type CalorieFloorChoice = CalorieFloor

export const CALORIE_FLOOR_LABELS: Record<CalorieFloorChoice, string> = {
  standard: 'Standard floor',
  low: 'Low floor',
}

export const PROTEIN_INTAKE_LABELS: Record<ProteinIntake, string> = {
  low: 'Low',
  moderate: 'Moderate',
  high: 'High',
  'extra-high': 'Extra high',
}

export const CALORIE_LEVEL_LABELS: Record<CalorieLevel, string> = {
  low: 'Low',
  normal: 'Level',
  high: 'High',
}

/** What one day of a goal's week asks for. */
export interface DayTargets {
  readonly calories: number
  readonly protein: number
  readonly carbs: number
  readonly fat: number
}

/**
 * One set of targets a goal asks of a day, standing from a day on.
 *
 * A goal carries a run of them: the ones settled when it was set, and one more for every
 * weekly check-in whose new numbers were taken. The check-in moves what a day is asked for and
 * nothing about what is being aimed at, so it adds here rather than starting a goal — which is
 * what it used to do, filling the goals gone by with copies of the goal still being pursued.
 */
export interface Targets {
  readonly id: string
  readonly effectiveFrom: string
  readonly calories: number
  readonly protein: number
  readonly carbs: number
  readonly fat: number
  readonly expenditureAtSet: number | null
  /** Seven days' targets, Monday first, or null where every day is the same. */
  readonly dayTargets: readonly DayTargets[] | null
  readonly createdAt: number
}

/**
 * What somebody is aiming at, from the day they set it until they replace it or end it. What
 * it asks of a day is its run of targets, and never the goal itself: nothing here moves of its
 * own accord.
 */
export interface Goal {
  readonly id: string
  readonly effectiveFrom: string
  readonly mode: GoalMode
  readonly aim: Aim | null
  readonly targetWeightKg: number | null
  readonly rateKgPerWeek: number | null
  /** 1 for Monday through 7 for Sunday. */
  readonly weeklyCheckinWeekday: number
  readonly diet: Diet | null
  readonly calorieFloor: CalorieFloorChoice | null
  readonly proteinIntake: ProteinIntake | null
  /** Seven levels, Monday first, or null on a goal whose week is even. */
  readonly calorieDistribution: readonly CalorieLevel[] | null
  /** The targets it asks of a day, oldest first, the first standing from the goal's own day. */
  readonly targets: readonly Targets[]
  /** The day the goal was ended on, or null on one still in force or replaced by a later one. */
  readonly endedOn: string | null
  readonly createdAt: number
}

/**
 * A goal together with what it asks of one particular day — the run resolved down to four
 * numbers. Everything that reads a target reads one of these, so no caller has to know that
 * the targets have a history at all.
 */
export type GoalOnDay = Goal &
  Omit<Targets, 'id' | 'effectiveFrom' | 'createdAt'> & {
    /** The day the targets in force began on, which is not the day the goal did. */
    readonly targetsFrom: string
  }

/** The targets a goal asks of a day: the newest of its run beginning on or before that day. */
export function targetsOn(goal: Goal, day: string): Targets | null {
  let found: Targets | null = null
  for (const targets of goal.targets) {
    if (targets.effectiveFrom <= day) {
      found = targets
    }
  }
  // A goal's first targets begin on the day the goal does, so this only stands in for a day
  // before the goal itself — which no caller asks about, `goalOn` having ruled it out already.
  return found ?? goal.targets[0] ?? null
}

/**
 * The goal as it stands on one day: what it aims at, and the four targets that day is asked
 * for — the run resolved to the set in force, and then the weekday's own where the week's days
 * differ. A goal whose week is even is asked the same thing every day.
 */
export function goalOnDay(goal: Goal, day: string): GoalOnDay | null {
  const targets = targetsOn(goal, day)
  if (targets === null) {
    return null
  }
  const { id: _id, effectiveFrom: targetsFrom, createdAt: _createdAt, ...numbers } = targets
  const stands: GoalOnDay = { ...goal, ...numbers, targetsFrom }
  const days = targets.dayTargets
  if (days?.length !== 7) {
    return stands
  }
  const weekday = weekdayOf(dayStart(day))
  const ofTheDay = days[weekday - 1]
  return ofTheDay === undefined ? stands : { ...stands, ...ofTheDay }
}

/**
 * What one day came to, without its entries: how many there are, what they add up to and
 * when the day was last touched. A few dozen bytes for a day whose entries are kilobytes,
 * so every day ever logged is held this way and the entries are read a day at a time.
 */
export interface DaySummary {
  readonly day: string
  readonly count: number
  /** The latest of its entries' own, and the empty string on a day holding none. */
  readonly updatedAt: string
  readonly nutrients: Nutrients
}

/**
 * The diary's shape, as one answer from the server: the days summed, never their entries. The
 * expenditure is not in it — see `expenditure.ts`, which works it out from this, the
 * weigh-ins and the body stats.
 */
export interface NutritionData {
  /** Every day holding anything, oldest first. */
  readonly days: readonly DaySummary[]
  readonly incompleteDays: readonly string[]
  /** Oldest first. */
  readonly goals: readonly Goal[]
  readonly weeklyCheckinOn: string | null
}

export type Sex = 'female' | 'male'

export const ACTIVITY_LEVELS = ['sedentary', 'light', 'moderate', 'active'] as const

export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number]

export const ACTIVITY_LABELS: Record<ActivityLevel, string> = {
  sedentary: 'Sedentary',
  light: 'Light',
  moderate: 'Moderate',
  active: 'Active',
}

/** The share of body weight a week past which a rate is warned about. */
export const RATE_WARNING_SHARE = 0.01

/** The calorie target under which a goal is warned about. */
export const CALORIE_FLOOR = 1200

/**
 * What a quick add is called until it is called something else. A quick add is most often a
 * number somebody wants on the day and nothing else, and a name is the one thing standing
 * between them and it — so the form opens on this, selected, and it costs a keystroke to be
 * rid of and nothing at all to keep.
 */
export const QUICK_ADD_NAME = 'Quick entry'

/** How many of a recipe's ingredients its suggested name names before it starts counting. */
const RECIPE_NAME_PARTS = 3

/**
 * What a recipe made out of things already logged is called until it is called something else:
 * what it is made of, in the order it was made of it. A recipe put together out of a morning's
 * entries is the morning, and naming it after them is both truer and quicker than the empty
 * field that stood there before.
 *
 * Past a handful of ingredients the names stop and the rest are counted, since a name is a
 * thing to recognise a recipe by rather than its contents written out.
 */
export function recipeNameFrom(parts: readonly string[]): string {
  const named = parts.map((part) => part.trim()).filter((part) => part !== '')
  if (named.length === 0) {
    return ''
  }
  if (named.length <= RECIPE_NAME_PARTS) {
    return named.join(' + ')
  }
  const rest = named.length - (RECIPE_NAME_PARTS - 1)
  return `${named.slice(0, RECIPE_NAME_PARTS - 1).join(' + ')} + ${String(rest)} more`
}

export const EMPTY_NUTRIENTS: Nutrients = {
  calories: 0,
  protein: null,
  carbs: null,
  fat: null,
  fibre: null,
  sugar: null,
  saturatedFat: null,
  sodium: null,
}

/** Which measure a food's own units are — grams where it is stated by weight, millilitres by volume. */
export function measureOf(food: Pick<Food, 'reference' | 'portions'>): 'weight' | 'volume' {
  return MEASURES[bulkUnitOf(food)].measure
}

/**
 * An amount already in base units, brought into the food's own measure. Where it is stated
 * in the other measure it crosses through the density — the nutrition spec, Units. A food
 * with no density is never offered those units; an amount that arrives in one anyway, off
 * another app's export, is taken one for one, a millilitre weighing a gram.
 */
function inOwnMeasure(food: Food, taken: number, measure: 'weight' | 'volume'): number {
  if (measure === measureOf(food) || food.density === null) {
    return taken
  }
  return measureOf(food) === 'weight' ? taken * food.density : taken / food.density
}

/** The same crossing the other way: an amount in the food's own measure, said in [measure]. */
function outOfOwnMeasure(food: Food, taken: number, measure: 'weight' | 'volume'): number {
  if (measure === measureOf(food) || food.density === null) {
    return taken
  }
  return measureOf(food) === 'weight' ? taken / food.density : taken * food.density
}

/**
 * What an amount weighs or measures, in the food's own units. An amount in a unit of the
 * other measure is worked out through the density — the nutrition spec, Units. A portion is
 * taken at the amount it states, whichever measure that is in: the spec's rule is about the
 * units an amount is typed in, and the server and Android read a portion the same way, so
 * crossing one here alone would move the figure between the form and what comes back.
 */
export function gramsOf(food: Food, amount: Amount): number | null {
  if (amount.unit !== 'portion') {
    const taken = amount.value * MEASURES[amount.unit].per
    return inOwnMeasure(food, taken, MEASURES[amount.unit].measure)
  }
  const portion = food.portions.find(
    (each) => each.name.toLowerCase() === (amount.portion ?? '').toLowerCase(),
  )
  return portion === undefined ? null : amount.value * portion.amount
}

/** How much of the reference amount an amount of a food is, or null where it cannot be told. */
export function factorOf(food: Food, amount: Amount): number | null {
  const grams = gramsOf(food, amount)
  if (grams === null) {
    return null
  }
  const reference = food.reference === 'portion' ? food.portions[0]?.amount : 100
  if (reference === undefined || reference <= 0) {
    return null
  }
  return grams / reference
}

export function scaled(nutrients: Nutrients, factor: number): Nutrients {
  const out: Record<string, number | null> = {}
  for (const name of NUTRIENTS) {
    const value = nutrients[name]
    out[name] = value === null ? null : Math.round(value * factor * 100) / 100
  }
  return out as Nutrients
}

export function nutrientsFor(food: Food, amount: Amount): Nutrients | null {
  const factor = factorOf(food, amount)
  return factor === null ? null : scaled(food.nutrients, factor)
}

/** What logging one entry says: a food in an amount, or a name with numbers of its own. */
export interface EntryLogging {
  readonly day: string
  readonly time: string
  readonly name?: string
  readonly food: Food | null
  readonly amount: Amount
  /** A quick entry's own numbers; a food's are worked out from the amount. */
  readonly nutrients?: Partial<Nutrients> & { calories: number }
  /** Logged through a meal: the foods-logged-before list skips it. */
  readonly fromMeal?: boolean
  /** Which meal it was logged from, where the account keeps that meal. */
  readonly mealId?: string | null
}

/**
 * An entry as it is logged, made on the device before the server has heard of it — the
 * nutrition spec, Logging an entry. The key that logs never waits on an answer, so the row the
 * day stands on has to be worked out here: a food's figures are this much of it by the same
 * arithmetic the server does, and a quick entry's are what was typed. The server's own row
 * takes its place with the next pull.
 */
export function entryLogged(logging: EntryLogging, at: number = Date.now()): Entry {
  const { food, amount } = logging
  const named = logging.name?.trim() ?? ''
  return {
    id: crypto.randomUUID(),
    day: logging.day,
    time: logging.time,
    name: food === null || named !== '' ? named : food.name,
    food,
    amount,
    nutrients:
      food === null
        ? { ...EMPTY_NUTRIENTS, ...logging.nutrients }
        : (nutrientsFor(food, amount) ?? EMPTY_NUTRIENTS),
    createdAt: at,
    updatedAt: updatedAtOf(at),
    ...(logging.fromMeal === true ? { fromMeal: true } : {}),
    ...(typeof logging.mealId === 'string' ? { mealId: logging.mealId } : {}),
  }
}

/**
 * The body an entry held is written with, so what is sent is what the day shows — a queued
 * log, or an undo putting a row back the way it was.
 */
export function entryBody(entry: Entry): PutEntryBody {
  return {
    day: entry.day,
    time: entry.time,
    name: entry.name,
    food: entry.food,
    amount: entry.amount,
    // A quick entry carries its own numbers; a food's are worked out from the amount.
    ...(entry.food === null ? { nutrients: entry.nutrients } : {}),
    ...(entry.fromMeal === true ? { fromMeal: true } : {}),
    ...(typeof entry.mealId === 'string' ? { mealId: entry.mealId } : {}),
    ...(entry.createdAt > 0 ? { createdAt: entry.createdAt } : {}),
  }
}

/**
 * The amount of a food that comes to [figure] of [nutrient] — the other half of the arithmetic
 * above, and what lets a figure be typed in place of an amount. Null where the food holds none
 * of that nutrient, since no amount of nothing comes to anything.
 *
 * [unit] and [portion] say what the answer is wanted in, so typing 500 kcal against a food
 * being logged in slices arrives at a number of slices.
 */
export function amountForNutrient(
  food: Food,
  nutrient: Nutrient,
  figure: number,
  unit: AmountUnit,
  portion: string | null,
): number | null {
  const per = food.nutrients[nutrient]
  if (per === null || per <= 0) {
    return null
  }
  const reference = food.reference === 'portion' ? food.portions[0]?.amount : 100
  if (reference === undefined || reference <= 0) {
    return null
  }
  // How many reference amounts come to the figure, and what that weighs in the food's units.
  const grams = (figure / per) * reference
  if (unit !== 'portion') {
    // Back into the unit it is being asked in: a figure that comes to 480 ml is two cups.
    // A unit of the other measure crosses through the density, the way `gramsOf` crosses
    // the other way — otherwise the figure typed against a liquid comes back as a different one.
    const taken = outOfOwnMeasure(food, grams, MEASURES[unit].measure)
    return Math.round((taken / MEASURES[unit].per) * 100) / 100
  }
  const held = food.portions.find(
    (each) => each.name.toLowerCase() === (portion ?? '').toLowerCase(),
  )
  if (held === undefined || held.amount <= 0) {
    return null
  }
  return Math.round((grams / held.amount) * 100) / 100
}

/**
 * Nutrients summed. A part that never said a nutrient counts as none of it — one bread with
 * no protein figure must not blank a whole day's. A nutrient is unknown on the whole only
 * where no part said it at all.
 */
export function summed(parts: readonly Nutrients[]): Nutrients {
  const out: Record<string, number | null> = { calories: 0 }
  for (const name of NUTRIENTS) {
    if (name === 'calories') {
      continue
    }
    out[name] = parts.every((part) => part[name] === null)
      ? null
      : Math.round(parts.reduce((sum, part) => sum + (part[name] ?? 0), 0) * 100) / 100
  }
  out.calories = Math.round(parts.reduce((sum, part) => sum + part.calories, 0) * 100) / 100
  return out as Nutrients
}

/**
 * A day's total, summed the way a recipe is not: a nutrient nobody knew on one entry is left
 * out of the sum rather than making the whole day unknown, since the day is read for what it
 * held rather than for what it might have.
 */
export function dayTotal(entries: readonly Entry[]): Nutrients {
  const out: Record<string, number | null> = { calories: 0 }
  for (const name of NUTRIENTS) {
    const known = entries.filter((entry) => entry.nutrients[name] !== null)
    out[name] =
      name !== 'calories' && known.length === 0
        ? null
        : Math.round(known.reduce((sum, entry) => sum + (entry.nutrients[name] ?? 0), 0) * 100) /
          100
  }
  return out as Nutrients
}

/**
 * What the whole of a recipe weighs, in grams: what whoever wrote it said, or what its
 * ingredients came to where nobody said — the nutrition spec, What a recipe weighs.
 *
 * A weight said by hand is the truer of the two, since it was read off scales and what comes out
 * of a pan is not what went into it.
 */
export function recipeGrams(recipe: Recipe): number {
  if (recipe.totalGrams != null && recipe.totalGrams > 0) {
    return recipe.totalGrams
  }
  return recipe.ingredients.reduce((sum, ingredient) => sum + ingredientWeight(ingredient), 0)
}

/**
 * What one ingredient contributes to that weight, in grams. A volume of a food that knows its
 * density weighs what that volume weighs, so a recipe's weight is the same number here, on the
 * server and on Android. Where nothing can weigh it, what it says at face value: a millilitre
 * for a gram, a portion at the amount it states.
 */
function ingredientWeight(held: Ingredient): number {
  const food = held.food
  if (held.amount.unit === 'portion') {
    const portion = food?.portions.find(
      (each) => each.name.toLowerCase() === (held.amount.portion ?? '').toLowerCase(),
    )
    if (portion === undefined) {
      return 0
    }
    const density = food?.density
    return portion.unit !== 'g' && density != null && density > 0
      ? held.amount.value * portion.amount * density
      : held.amount.value * portion.amount
  }
  const taken = held.amount.value * MEASURES[held.amount.unit].per
  if (MEASURES[held.amount.unit].measure === 'weight') {
    return taken
  }
  const density = food?.density
  return density != null && density > 0 ? taken * density : taken
}

/**
 * Whether a food can be logged by weight at all. Everything can, except a recipe whose whole
 * weighs nothing — every ingredient counted in portions nobody knows the weight of. Its serving
 * stands at a nominal gram so a count of servings still works, and a weight of it would be read
 * against that gram, which is a number nobody meant. Such a recipe is logged by servings alone.
 */
export function takesAWeight(food: Food): boolean {
  return food.recipe === null || recipeGrams(food.recipe) > 0
}

/**
 * A recipe worked out from its ingredients, as the server does it: the nutrients per serving,
 * the serving as the first portion.
 */
export function recipeFood(
  base: Pick<Food, 'id' | 'name' | 'emoji' | 'brand' | 'barcode'> &
    Partial<Pick<Food, 'broughtFrom'>>,
  recipe: Recipe,
): Food {
  const total = summed(recipe.ingredients.map((ingredient) => ingredient.nutrients))
  const grams = recipeGrams(recipe)
  const perServing = scaled(total, 1 / recipe.servings)
  const servingGrams = grams > 0 ? Math.round((grams / recipe.servings) * 10) / 10 : 0
  return {
    ...base,
    source: 'own',
    library: null,
    libraryCode: null,
    broughtFrom: base.broughtFrom ?? null,
    reference: 'portion',
    nutrients: perServing,
    portions: [{ name: 'serving', amount: servingGrams > 0 ? servingGrams : 1, unit: 'g' }],
    density: null,
    recipe,
  }
}

/**
 * What the coached way arrives at: the week's four targets, the seven days it is made of, the
 * range a maintaining goal moves within, and the expenditure and body weight they were worked
 * out from — the same numbers on every app, per the nutrition spec.
 */
export interface GuidedTargets extends CoachedWeek {
  readonly expenditure: number
  readonly weightKg: number
}

/** Whether a week's seven days all ask for the same thing, and so need not be kept apart. */
export function isEvenWeek(days: readonly DayTargets[]): boolean {
  const first = days[0]
  return (
    first !== undefined &&
    days.every(
      (day) =>
        day.calories === first.calories &&
        day.protein === first.protein &&
        day.carbs === first.carbs &&
        day.fat === first.fat,
    )
  )
}

/**
 * The goal in force on a day: the newest one that had begun by then, or none — and with the
 * targets that day's weekday carries, so nothing downstream has to ask twice.
 */
export function goalOn(goals: readonly Goal[], day: string): GoalOnDay | null {
  let found: GoalOnDay | null = null
  for (const goal of goals) {
    if (goal.effectiveFrom <= day) {
      // A goal ended stands over the days before it ended and over none after them.
      found = goal.endedOn !== null && goal.endedOn <= day ? null : goalOnDay(goal, day)
    }
  }
  return found
}

/** The goal itself as it stands today: the last one set, unless it was ended. */
export function currentGoal(goals: readonly Goal[]): Goal | null {
  const newest = goals[goals.length - 1]
  return newest?.endedOn == null ? (newest ?? null) : null
}

/**
 * Whether a weight has crossed a goal's target, in the direction the goal aims — down through
 * it where the goal loses, up through it where the goal gains. A goal that maintains is never
 * reached, there being nothing to arrive at. See the nutrition spec, Reaching it.
 */
export function crossedTarget(goal: Goal, weightKg: number): boolean {
  if (goal.targetWeightKg === null) {
    return false
  }
  return goal.aim === 'lose'
    ? weightKg <= goal.targetWeightKg
    : goal.aim === 'gain'
      ? weightKg >= goal.targetWeightKg
      : false
}

/** The goal in force with what it asks of today — what every reader of a target wants. */
export function currentGoalOn(goals: readonly Goal[], today: string): GoalOnDay | null {
  const goal = currentGoal(goals)
  return goal === null ? null : goalOnDay(goal, today)
}

/**
 * A day's entries, in the order they were eaten: by time, then by when each was written, then
 * by id — the order the phone keeps them in, so entries of one minute stand one way everywhere
 * rather than however the mirror happened to hand them over.
 */
export function entriesOn(entries: readonly Entry[], day: string): Entry[] {
  return entries
    .filter((entry) => entry.day === day)
    .sort((a, b) =>
      a.time !== b.time
        ? a.time < b.time
          ? -1
          : 1
        : a.createdAt !== b.createdAt
          ? a.createdAt - b.createdAt
          : a.id < b.id
            ? -1
            : a.id > b.id
              ? 1
              : 0,
    )
}

/** Every day holding an entry, oldest first. */
export function loggedDays(data: Pick<NutritionData, 'days'>): string[] {
  return data.days.map((each) => each.day).sort()
}

/** What a day came to, or null on a day that holds nothing. */
export function totalOn(data: Pick<NutritionData, 'days'>, day: string): Nutrients | null {
  return data.days.find((each) => each.day === day)?.nutrients ?? null
}

/** A day's entries summed up as the server would sum them, for a day changed here. */
export function summaryOf(day: string, entries: readonly Entry[]): DaySummary {
  return {
    day,
    count: entries.length,
    updatedAt: entries.reduce((latest, entry) => laterOf(latest, entry.updatedAt), ''),
    nutrients: dayTotal(entries),
  }
}

/** How a day stands against its calorie target: within two per cent either way is on it. */
export type Standing = 'under' | 'over' | 'on'

export function standingOf(calories: number, target: number): Standing {
  if (target <= 0) {
    return 'on'
  }
  const share = (calories - target) / target
  if (Math.abs(share) <= 0.02) {
    return 'on'
  }
  return share < 0 ? 'under' : 'over'
}

export function standingSaid(standing: Standing): string {
  return standing === 'under' ? 'Under target' : standing === 'over' ? 'Over target' : 'On target'
}

/** A share of a target, as every reading of one is written: a whole per cent. */
export function formatShare(share: number): string {
  return `${String(Math.round(share * 100))}%`
}

/** A count of calories, whole and grouped. */
export function formatCalories(calories: number): string {
  return `${Math.round(calories).toLocaleString()} kcal`
}

/** A macro in grams, to the gram — nobody weighs their protein to a tenth. */
export function formatGrams(grams: number): string {
  return `${String(Math.round(grams))} g`
}

/** A nutrient as it reads, in its own unit. */
export function formatNutrient(name: Nutrient, value: number): string {
  if (name === 'calories') {
    return formatCalories(value)
  }
  const shown = name === 'sodium' ? Math.round(value) : Math.round(value * 10) / 10
  return `${String(shown)} ${NUTRIENT_UNITS[name]}`
}

/**
 * The one line anything says what it comes to on: "541 kcal · 65P 25C 21F". Always the
 * calories and always all three macros, in the one order the whole app keeps — protein,
 * carbs, fat; the app spec, Figures — so two lines under each other can be compared down the
 * column; a macro nothing is stated for stands at nothing rather than going missing and
 * shifting what follows it along.
 */
export function macroLine(nutrients: Nutrients): string {
  const macros = [
    `${String(Math.round(nutrients.protein ?? 0))}P`,
    `${String(Math.round(nutrients.carbs ?? 0))}C`,
    `${String(Math.round(nutrients.fat ?? 0))}F`,
  ]
  return `${formatCalories(nutrients.calories)} · ${macros.join(' ')}`
}

/** What an amount reads as: "150 g", "2 slices", "1 serving". */
export function amountSaid(amount: Amount): string {
  const value = Math.round(amount.value * 100) / 100
  if (amount.unit === 'portion') {
    const name = amount.portion ?? 'portion'
    return `${String(value)} ${value === 1 ? name : plural(name)}`
  }
  return `${String(value)} ${amount.unit}`
}

function plural(name: string): string {
  if (/(s|x|ch|sh)$/i.test(name)) {
    return `${name}es`
  }
  if (/[^aeiou]y$/i.test(name)) {
    return `${name.slice(0, -1)}ies`
  }
  return `${name}s`
}

/** What a food's reference amount is called: "100 g", "1 slice". */
export function referenceSaid(food: Pick<Food, 'reference' | 'portions'>): string {
  if (food.reference === 'portion') {
    return `1 ${food.portions[0]?.name ?? 'portion'}`
  }
  return food.reference === '100g' ? '100 g' : '100 ml'
}

/**
 * The units a food's weight or volume can be taken in, in the order they are offered: the
 * account's own first, so an account counting in pounds meets ounces where one counting in
 * kilograms meets grams. Every unit of the measure is offered to both — the setting says
 * which are likely, never which are allowed.
 */
export function unitsOf(
  food: Pick<Food, 'reference' | 'portions' | 'density'>,
  units: Units,
): BulkUnit[] {
  const imperial = units === 'lb'
  const ofMeasure = (measure: 'weight' | 'volume') =>
    (Object.keys(MEASURES) as BulkUnit[])
      .filter((unit) => MEASURES[unit].measure === measure)
      .sort((one, other) => rank(one, imperial) - rank(other, imperial))
  /*
   * A liquid — a food carrying a density — is taken by volume and by weight both, the volume
   * first, whatever it is stated per; any other food in the units of what it is stated per.
   */
  if (food.density !== null) {
    return [...ofMeasure('volume'), ...ofMeasure('weight')]
  }
  return ofMeasure(measureOf(food))
}

/** The unit a food is met in first: millilitres on a liquid, and its own units otherwise. */
export function firstUnitOf(food: Pick<Food, 'reference' | 'portions' | 'density'>): 'g' | 'ml' {
  return food.density !== null ? 'ml' : bulkUnitOf(food)
}

/**
 * The measures a density can be stated against on a food's form, in the order they are
 * offered — the nutrition spec, Own foods.
 */
export const DENSITY_MEASURES = ['ml', 'tsp', 'tbsp', 'fl oz', 'cup'] as const

export type DensityMeasure = (typeof DENSITY_MEASURES)[number]

/** What one of a measure of a food at this density weighs, in grams. */
export function weighsPerMeasure(density: number, measure: DensityMeasure): number {
  return Math.round(density * MEASURES[measure].per * 1000) / 1000
}

/**
 * The density a weight against one of the measures comes to, rounded to three places — the
 * nutrition spec, Own foods: a cup weighing 240 g is a density of 1. Null for a weight that
 * is not a number above nothing, which is no density at all.
 */
export function densityFromWeight(weight: number, measure: DensityMeasure): number | null {
  if (!Number.isFinite(weight) || weight <= 0) {
    return null
  }
  return Math.round((weight / MEASURES[measure].per) * 1000) / 1000
}

/** Where a unit stands in the row: the account's own kind first, and the largest of it last. */
function rank(unit: BulkUnit, imperial: boolean): number {
  const own = IMPERIAL.includes(unit) === imperial ? 0 : 100
  return own + (Object.keys(MEASURES) as BulkUnit[]).indexOf(unit)
}

const IMPERIAL: BulkUnit[] = ['oz', 'lb', 'fl oz', 'cup']

/** The unit a food's weight or volume is stated in. */
export function bulkUnitOf(food: Pick<Food, 'reference' | 'portions'>): 'g' | 'ml' {
  if (food.reference === '100ml') {
    return 'ml'
  }
  if (food.reference === 'portion') {
    return food.portions[0]?.unit ?? 'g'
  }
  return 'g'
}

/**
 * Which database a library food came from, as the apps name it. Named rather than called a
 * library: it is somebody else's database, and saying whose is what tells you how much to
 * trust a figure nobody here typed.
 */
export function librarySaid(food: Pick<Food, 'library'>): string {
  return food.library === 'usda' ? 'USDA' : 'Open Food Facts'
}

/**
 * Where a found food comes from, as a search result says it. A food brought in from another
 * app says nothing here but that it is the account's: the badge's job in a list is to say
 * whether the food is theirs to change, and it is. Where it came from is on its own page.
 */
export function sourceSaid(food: FoundFood): string {
  return food.source === 'library' ? librarySaid(food) : 'Mine'
}

/**
 * A food that came in from another app and has no row of its own anywhere: the copy an
 * imported entry keeps, and the only record of it there is. It is offered nowhere a food is
 * picked, and never written into a fresh entry — the nutrition spec, Own foods.
 *
 * A food the import did make a row for is not this: it is the account's own, offered and
 * logged like anything else, however it arrived.
 */
export function isOnlyBroughtIn(food: Pick<Food, 'id' | 'broughtFrom'>): boolean {
  return food.broughtFrom !== null && food.id === null
}

/** The apps whose exports can be brought in, as each is written out. */
const BROUGHT_FROM_NAMES: Record<string, string> = { macrofactor: 'MacroFactor' }

/**
 * That a food came in from another app, where it did — for its own page, which is the one
 * place this is said. Null on everything made here.
 */
export function broughtFromSaid(food: Pick<Food, 'broughtFrom'>): string | null {
  if (food.broughtFrom === null) {
    return null
  }
  return `Brought in from ${BROUGHT_FROM_NAMES[food.broughtFrom] ?? food.broughtFrom}`
}

/** The key one food is known by wherever it is met again: its own id, or its library code. */
export function foodKey(food: Food): string {
  return food.id ?? `library:${food.libraryCode ?? food.name}`
}

/** The time of day now, as `HH:MM`. */
export function nowTime(now: Date = new Date()): string {
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`
}

/** A time of day as `HH:MM`, read as minutes into the day. */
export function minutesOf(time: string): number {
  const [hours = 0, minutes = 0] = time.split(':').map(Number)
  return hours * 60 + minutes
}

/** The time of day a moment fell on, as an entry keeps it. */
export function timeOf(timestamp: number): string {
  return nowTime(new Date(timestamp))
}

/** The day this week's check-in falls on: the latest day on or before today wearing that weekday. */
export function weeklyCheckinDayOf(weekday: number, today: string): string {
  const at = new Date(dayStart(today))
  const on = ((at.getDay() + 6) % 7) + 1
  at.setDate(at.getDate() - ((on - weekday + 7) % 7))
  return dayKey(at.getTime())
}

/** Whether the weekly check-in day has come round unanswered: today is on or past this week's
 * check-in day and it has not been answered on or after that day. What is raised waits on a weight
 * too — see `weeklyCheckinDue`. */
export function weeklyCheckinReached(data: NutritionData, today: string): boolean {
  const goal = currentGoal(data.goals)
  // A goal set by hand is not coached, so there is nothing weekly to look at again.
  if (goal?.aim == null || goal.mode !== 'guided') {
    return false
  }
  const weeklyCheckinDay = weeklyCheckinDayOf(goal.weeklyCheckinWeekday, today)
  // A goal set on or after this week's check-in day has just been arrived at, so there is
  // nothing yet to look at again: the check-in waits for the next week.
  if (weeklyCheckinDay <= goal.effectiveFrom) {
    return false
  }
  return data.weeklyCheckinOn === null || data.weeklyCheckinOn < weeklyCheckinDay
}

/** Whether the weekly check-in is due to be raised: its day has come round unanswered, and a weigh-in
 * carrying a weight has been made on that day or after it. The check-in recalculates off the weight,
 * so it waits for one rather than opening on the week that has not been weighed yet. */
export function weeklyCheckinDue(
  data: NutritionData,
  today: string,
  weighIns: readonly { readonly day: string; readonly weightKg: number | null }[],
): boolean {
  if (!weeklyCheckinReached(data, today)) {
    return false
  }
  const goal = currentGoal(data.goals)
  if (goal == null) {
    return false
  }
  const checkinDay = weeklyCheckinDayOf(goal.weeklyCheckinWeekday, today)
  return weighIns.some((weighIn) => weighIn.weightKg !== null && weighIn.day >= checkinDay)
}

/**
 * How far the trend weight stands from a target, and the day it is expected on at the rate the
 * trend has moved over the last fortnight. A trend standing still or moving away names no day.
 */
export interface Progress {
  readonly trendKg: number
  readonly targetKg: number
  /** Signed: negative where the trend is below the target. */
  readonly distanceKg: number
  readonly expectedDay: string | null
}

export function progressToward(
  trend: readonly { day: string; value: number }[],
  targetKg: number,
  today: string,
): Progress | null {
  const latest = trend[trend.length - 1]
  if (latest === undefined) {
    return null
  }
  const distanceKg = latest.value - targetKg
  const edgeAt = new Date(dayStart(latest.day))
  edgeAt.setDate(edgeAt.getDate() - 14)
  const edge = dayKey(edgeAt.getTime())
  const back = [...trend].reverse().find((point) => point.day <= edge) ?? trend[0]
  let expectedDay: string | null = null
  if (back !== undefined && back.day !== latest.day) {
    const days = Math.max(1, Math.round((dayStart(latest.day) - dayStart(back.day)) / 86_400_000))
    const perDay = (latest.value - back.value) / days
    // Toward the target means moving the opposite way from where it stands from it.
    if (perDay !== 0 && Math.sign(perDay) === -Math.sign(distanceKg) && distanceKg !== 0) {
      const wait = Math.ceil(Math.abs(distanceKg / perDay))
      const at = new Date(dayStart(today))
      at.setDate(at.getDate() + wait)
      expectedDay = dayKey(at.getTime())
    }
  }
  return { trendKg: latest.value, targetKg, distanceKg, expectedDay }
}

/** How far a distance reads, in the account's units: "2.3 kg to go". */
export function distanceSaid(distanceKg: number, units: Units): string {
  const shown = Math.abs(kgToDisplay(distanceKg, units))
  return shown === 0 ? 'At target' : `${String(shown)} ${units} to go`
}

/*
 * Setting a goal is a process of steps — see the nutrition spec, The goal. What follows is
 * the arithmetic those steps are made of, kept away from the page that draws them so that a
 * rate written four ways, or a target's expected day, can be read the same in a test.
 */

/** The steps a goal can be set through, in the order they are asked. */
export type GoalStep =
  | 'way'
  | 'aim'
  | 'rate'
  | 'maintain'
  | 'diet'
  | 'floor'
  | 'protein'
  | 'distribution'
  | 'spread'
  | 'manual-targets'
  | 'review'

/** What the process has been answered with so far, as far as which steps it asks. */
export interface GoalPath {
  readonly mode: GoalMode
  readonly aim: Aim
  /** Whether a goal set by hand carries the same targets every day. */
  readonly sameAllWeek: boolean
}

/** The steps this path asks, first to last. The review is always the last of them. */
export function goalSteps(path: GoalPath): GoalStep[] {
  if (path.mode === 'manual') {
    return ['way', 'spread', 'manual-targets', 'review']
  }
  return [
    'way',
    'aim',
    path.aim === 'maintain' ? 'maintain' : 'rate',
    'diet',
    'floor',
    'protein',
    'distribution',
    'review',
  ]
}

/** What each step is called where it is named — its own head, and the review that lists it. */
export const GOAL_STEP_TITLES: Record<GoalStep, string> = {
  way: 'How this goal is set',
  aim: 'What you are aiming at',
  rate: 'Target weight and rate',
  maintain: 'Target trend weight',
  diet: 'Diet',
  floor: 'Calorie floor',
  protein: 'Protein intake',
  distribution: 'Calorie distribution',
  spread: 'How the week is set',
  'manual-targets': 'Targets',
  review: 'Review your program',
}

/** A rate in kilograms a week as a share of the body weight, per cent, or null without one. */
export function ratePercentOf(rateKgPerWeek: number, weightKg: number | null): number | null {
  return weightKg === null || weightKg <= 0 ? null : (rateKgPerWeek / weightKg) * 100
}

/** The fastest a rate is offered: a share of the body weight a week. */
export const RATE_CEILING_SHARE = 0.015

/** How many weeks a month is taken to be where a rate is written per month. */
export const WEEKS_PER_MONTH = 52 / 12

/**
 * The day a target weight is expected on at a rate, counted from today: the gap between the
 * weight and the target, at the rate asked for. A rate of nothing arrives nowhere.
 */
export function expectedOnAtRate(
  weightKg: number | null,
  targetWeightKg: number | null,
  rateKgPerWeek: number | null,
  today: string,
): string | null {
  if (weightKg === null || targetWeightKg === null || rateKgPerWeek === null) {
    return null
  }
  const gap = Math.abs(weightKg - targetWeightKg)
  if (gap === 0 || rateKgPerWeek <= 0) {
    return null
  }
  const days = Math.ceil((gap / rateKgPerWeek) * 7)
  // Counted in calendar days rather than in milliseconds: a day is not always 86,400,000 of
  // them, and a forecast that crosses a clock change must not land a day early.
  const at = new Date(dayStart(today))
  at.setDate(at.getDate() + days)
  return dayKey(at.getTime())
}
